
import React, { useState, useEffect } from 'react';
import { googleAuth } from '../utils/googleAuth';
import LegalModal from './LegalModal';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';

interface LandingPageProps {
  acceptedTerms: boolean;
  setAcceptedTerms: (val: boolean) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ acceptedTerms, setAcceptedTerms, theme, toggleTheme }) => {
  const [legalModal, setLegalModal] = useState<{ isOpen: boolean; type: 'terms' | 'privacy' }>({
    isOpen: false,
    type: 'terms'
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const instagramUrl = import.meta.env.VITE_INSTAGRAM_URL || '#';

  const handleAccessClick = () => {
    if (acceptedTerms) {
      googleAuth.login();
    } else {
      setShowLoginModal(true);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300">
      
      {/* Premium Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <span className="text-lg font-black uppercase tracking-tighter dark:text-white">Equilibra</span>
          </div>
          
          <div className="flex items-center gap-4">
            <ThemeToggle theme={theme} toggleTheme={toggleTheme} className="scale-90" />
            <button 
              onClick={handleAccessClick}
              className="hidden md:block px-6 py-2.5 bg-slate-900 dark:bg-blue-600 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-lg shadow-blue-500/20"
            >
              Acessar Sistema
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1 flex flex-col items-center relative overflow-hidden pt-16">
        
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-500/10 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-emerald-500/10 rounded-full blur-[100px]"></div>
        </div>

        {/* Main Hero Content */}
        <div className="w-full max-w-6xl px-6 py-12 md:py-20 flex flex-col items-center text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30 text-blue-600 dark:text-blue-400 text-[10px] font-black uppercase tracking-widest mb-8 animate-bounce">
            <i className="fa-solid fa-sparkles"></i>
            <span>Soberania Financeira 2.0</span>
          </div>

          <h1 className="text-6xl md:text-8xl font-black text-slate-900 dark:text-white mb-8 uppercase tracking-tighter leading-[0.9]">
            Domine suas <br/>
            <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500 pr-4 pb-2">Finanças</span>
          </h1>
          
          <p className="text-slate-500 dark:text-slate-400 text-lg md:text-xl mb-12 font-medium italic max-w-2xl mx-auto leading-relaxed">
            Gestão profissional com soberania absoluta. <br/>
            <span className="text-sm not-italic font-bold uppercase tracking-[0.3em] text-blue-600 dark:text-blue-400 mt-4 block">Seus dados nunca saem da sua conta Google.</span>
          </p>

          <div className="w-full max-w-[400px] space-y-6">
            <button 
              onClick={handleAccessClick}
              className="w-full py-6 bg-slate-900 dark:bg-blue-600 text-white rounded-[2rem] font-black text-[12px] uppercase tracking-[0.3em] shadow-2xl shadow-blue-500/30 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-4 group"
            >
              <span>Começar Agora</span>
              <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>
        </div>

        {/* Features Section */}
        <section className="w-full max-w-6xl px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
          <div className="group bg-white dark:bg-slate-900 p-10 rounded-[3rem] border border-slate-100 dark:border-white/5 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
            <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mb-8 group-hover:rotate-12 transition-transform">
              <i className="fa-solid fa-shield-halved text-2xl"></i>
            </div>
            <h3 className="text-xl font-black uppercase tracking-tight mb-4 dark:text-white">Soberania Total</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
              Diferente de outros apps, nós não guardamos seus dados. Tudo é salvo na sua própria planilha do Google Sheets. Você é o único dono.
            </p>
          </div>

          <div className="group bg-white dark:bg-slate-900 p-10 rounded-[3rem] border border-slate-100 dark:border-white/5 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
            <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-8 group-hover:rotate-12 transition-transform">
              <i className="fa-solid fa-lock text-2xl"></i>
            </div>
            <h3 className="text-xl font-black uppercase tracking-tight mb-4 dark:text-white">Zero Knowledge</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
              Criptografia de nível bancário. Nem nossa equipe consegue ler seus dados. Sua Chave Mestra é o único segredo que abre seu cofre.
            </p>
          </div>

          <div className="group bg-white dark:bg-slate-900 p-10 rounded-[3rem] border border-slate-100 dark:border-white/5 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
            <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mb-8 group-hover:rotate-12 transition-transform">
              <i className="fa-solid fa-bolt text-2xl"></i>
            </div>
            <h3 className="text-xl font-black uppercase tracking-tight mb-4 dark:text-white">Gestão Ágil</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
              Dashboards dinâmicos, controle de faturas, fluxo de caixa e integração inteligente com calendário. Tudo em um só lugar.
            </p>
          </div>
        </section>

        {/* Accessible Commitment Section */}
        <section className="w-full max-w-4xl px-6 py-10 text-center relative z-10">
          <div className="bg-blue-600/5 dark:bg-blue-400/5 border border-blue-100 dark:border-blue-900/30 rounded-[2.5rem] p-8 md:p-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-[9px] font-black uppercase tracking-widest mb-6">
              <i className="fa-solid fa-hand-holding-heart"></i>
              <span>Compromisso com você</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight mb-4 dark:text-white">Tecnologia de Elite, Preço Justo</h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              O Equilibra foi projetado para ser uma ferramenta de soberania financeira acessível a todos. Mantemos um <strong>pequeno custo anual</strong> apenas para sustentar a infraestrutura, garantindo que a plataforma sempre seja de <strong>baixo custo e alta acessibilidade</strong> para quem busca controle real sobre seu patrimônio.
            </p>
          </div>
        </section>

        {/* How it Works Section */}
        <section className="w-full max-w-5xl px-6 py-16 text-center relative z-10">
          <h2 className="text-4xl font-black uppercase tracking-tight mb-20 dark:text-white">O Caminho para a Liberdade</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-slate-200 dark:bg-slate-800 -translate-y-1/2 z-0"></div>
            
            <div className="relative z-10 flex flex-col items-center">
              <div className="w-20 h-20 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center text-3xl font-black shadow-xl border-4 border-blue-500 mb-8 dark:text-white">1</div>
              <h4 className="font-black uppercase tracking-tight mb-3 dark:text-white">Conexão Google</h4>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Integração segura com seu Google Drive para criar sua base de dados e lembretes no Google Calendar.</p>
            </div>

            <div className="relative z-10 flex flex-col items-center">
              <div className="w-20 h-20 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center text-3xl font-black shadow-xl border-4 border-emerald-500 mb-8 dark:text-white">2</div>
              <h4 className="font-black uppercase tracking-tight mb-3 dark:text-white">Chave Mestra</h4>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Você gera sua chave única. Sem ela, os dados são apenas códigos ilegíveis.</p>
            </div>

            <div className="relative z-10 flex flex-col items-center">
              <div className="w-20 h-20 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center text-3xl font-black shadow-xl border-4 border-amber-500 mb-8 dark:text-white">3</div>
              <h4 className="font-black uppercase tracking-tight mb-3 dark:text-white">Evolução</h4>
              <p className="text-slate-500 dark:text-slate-400 text-sm">Visualize seu patrimônio crescer com ferramentas de análise profissional.</p>
            </div>
          </div>
        </section>

        {/* PWA Install Prompt */}
        {deferredPrompt && (
          <div className="w-full max-w-md px-6 pb-20 relative z-10">
            <button 
              onClick={handleInstallClick}
              className="w-full py-5 rounded-[2rem] font-black text-[11px] uppercase tracking-[0.25em] bg-white dark:bg-slate-900 text-blue-600 border-2 border-blue-100 dark:border-slate-800 shadow-2xl hover:bg-blue-50 transition-all flex items-center justify-center gap-4"
            >
              <i className="fa-solid fa-mobile-screen-button text-lg"></i>
              Instalar App no Celular
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-200/50 dark:border-white/5 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-6">
          
          <div className="flex flex-wrap justify-center gap-8 md:gap-12">
            {/* Instagram */}
            <a 
              href={instagramUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-slate-400 hover:text-pink-500 transition-all group"
            >
              <i className="fa-brands fa-instagram text-xl"></i>
              <span className="text-[10px] font-black uppercase tracking-widest">@equilibra_finance</span>
            </a>

            {/* Email Contact */}
            <button 
              onClick={() => {
                const email = import.meta.env.VITE_CONTACT_EMAIL || 'suporte@equilibra.com';
                window.location.href = `mailto:${email}`;
              }}
              className="flex items-center gap-3 text-slate-400 hover:text-blue-500 transition-all group"
            >
              <i className="fa-solid fa-envelope text-xl"></i>
              <span className="text-[10px] font-black uppercase tracking-widest">Falar com Suporte</span>
            </button>
          </div>
          
          {/* Legal Links */}
          <div className="flex gap-8 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <a href="/termos" className="hover:text-blue-500 transition-colors">Termos</a>
            <a href="/privacidade" className="hover:text-blue-500 transition-colors">Privacidade</a>
          </div>

          {/* Copyright */}
          <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em]">© 2026 • Equilibra Finance</p>
        </div>
      </footer>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setShowLoginModal(false)}></div>
          <div className="relative w-full max-w-[400px] bg-white dark:bg-slate-900 rounded-[3rem] shadow-2xl border border-slate-200 dark:border-white/5 overflow-hidden animate-in zoom-in duration-300">
            <div className="p-10 text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-[2rem] flex items-center justify-center text-white text-3xl mx-auto mb-8 shadow-xl shadow-blue-500/20">
                <i className="fa-solid fa-user-shield"></i>
              </div>
              
              <h2 className="text-2xl font-black uppercase tracking-tight mb-2 dark:text-white">Acesso Seguro</h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-10">Conecte sua conta Google para gerenciar seu patrimônio com soberania.</p>

              <div className="space-y-6">
                <button 
                  onClick={() => googleAuth.login()} 
                  disabled={!acceptedTerms} 
                  className={`w-full py-5 rounded-2xl font-black text-[11px] uppercase tracking-[0.25em] flex items-center justify-center gap-4 transition-all ${
                    acceptedTerms 
                      ? 'bg-slate-900 dark:bg-blue-600 text-white hover:scale-105 active:scale-95 shadow-xl' 
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  <i className="fa-brands fa-google text-lg"></i>
                  Entrar com Google
                </button>

                <div className="flex items-center gap-3 justify-center group cursor-pointer" onClick={() => setAcceptedTerms(!acceptedTerms)}>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${acceptedTerms ? 'bg-blue-600 border-blue-600' : 'border-slate-300 dark:border-slate-600'}`}>
                    {acceptedTerms && <i className="fa-solid fa-check text-white text-[10px]"></i>}
                  </div>
                  <label className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest cursor-pointer group-hover:text-slate-700 dark:group-hover:text-slate-200 transition-colors">
                    Li e aceito os <button onClick={(e) => { e.stopPropagation(); setLegalModal({ isOpen: true, type: 'terms' }); }} className="text-blue-500 underline">termos</button> e <button onClick={(e) => { e.stopPropagation(); setLegalModal({ isOpen: true, type: 'privacy' }); }} className="text-blue-500 underline">políticas</button>.
                  </label>
                </div>
              </div>
            </div>
            
            <button 
              onClick={() => setShowLoginModal(false)}
              className="w-full py-4 bg-slate-50 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      <LegalModal 
        isOpen={legalModal.isOpen} 
        onClose={() => setLegalModal({ ...legalModal, isOpen: false })} 
        type={legalModal.type} 
      />
    </div>
  );
};

export default LandingPage;